import * as React from "react";
import { homeLink, request, resourceLink } from "../../../../../services/cpln";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { CardCollapsibleContent } from "../../../../../components/antd/card/CardCollapsibleContent";
import { CaretRightOutlined, EllipsisOutlined, PlusOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { useAppSelector } from "../../../../../store/hooks";
import { selectOrg } from "../../../../../store/contextSlice";
import { NGProviderBrowser } from "../../../ngProviderBrowser";
import { ProviderPreInstallScript } from "../../../advanced/providerPreInstallScript";
import { ProviderAutoscaler } from "../../../advanced/providerAutoscaler";
import { ProviderNetworking } from "../../../advanced/providerNetworking";
import { inputToAzureProvider, inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { Mk8sCluster } from "../../../../../schema/types/mk8s/entity/mk8s";
import { getDefaultAzureInput } from "../../../draft/utils/utils";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpDescription } from "../../../../gvc/testNewForm/CpDescription";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpRadioGroup } from "../../../../gvc/testNewForm/CpRadioGroup";
import { CpSelect } from "../../../../gvc/testNewForm/CpSelect";
import { CpArrayString } from "../../../../gvc/testNewForm/CpArrayString";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { store } from "../../../../../store";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpErrorWarningArray } from "../../../../gvc/testNewForm/CpErrorWarningArray";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpDirtyIndicatorArrayIcon } from "../../../../gvc/testNewForm/CpDirtyIndicatorArrayIconLink";
import { CpKindSelect } from "../../../../gvc/testNewForm/temp/CpKindSelect";
import { useDrawerContext } from "../../../../../components/antd/drawerContext";
import { EmbeddedSecretCreate } from "../../../../secret/create/embedded";
import { parseLink } from "../../../../../utils/linkParser/linkParser";
import { CpLabelTooltip } from "../../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../../gvc/testNewForm/CpTooltip";
import { AzureInstructions, ProviderAzureInstructions } from "../../../provider_azure_instructions";

interface Props {}
export const AzureProviderSelf: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();
  const org = useAppSelector(selectOrg);
  const clusterName = useCpFormValue("name");

  const drawer = useDrawerContext();

  const [browserKey, setBrowserKey] = React.useState("");

  const [azureInstructions, setAzureInstructions] = React.useState<AzureInstructions | null>(null);
  const [azureInstructionsError, setAzureInstructionsError] = React.useState<string>("");

  const [recommendedAmis, setRecommendedAmis] = React.useState<any[]>([]);

  React.useEffect(() => {
    fetchRecommendedAmis();
    fetchAzureInstructions();
  }, []);

  async function fetchRecommendedAmis() {
    try {
      const cluster: Mk8sCluster = {
        name: "discover",
        spec: {
          provider: {
            azure: inputToAzureProvider(getDefaultAzureInput()),
          },
          version: "1.35.3",
        },
      } as any;
      const { data } = await request({
        service: "api",
        url: homeLink("mk8s") + "/-discover",
        method: "post",
        body: {
          objectType: "recommendedImages",
          cluster: cluster,
        },
      });
      const res = data.data.allow.map((r: any) => ({ label: r, value: r }));
      console.log("azure recommended amis res", res);
      setRecommendedAmis(res);
    } catch (e) {
      setRecommendedAmis([]);
    }
  }

  async function fetchAzureInstructions() {
    try {
      setAzureInstructionsError("");
      const { data } = await request({ url: `/org/${org}/mk8s/-instructions/azure` });
      setAzureInstructions(data);
    } catch (e) {
      setAzureInstructionsError(e.message);
    }
  }

  const [isAdvancedCollapsed, setIsAdvancedCollapsed] = React.useState(true);

  const imageType = useCpFormValue(`azure.image.type`);
  const location = useCpFormValue(`azure.location`);
  const resourceGroup = useCpFormValue(`azure.resourceGroup`);
  const networkId = useCpFormValue(`azure.networkId`);
  const subscriptionId = useCpFormValue(`azure.subscriptionId`);

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            Azure Provider
            <CpErrorWarningArrayIcon
              names={[
                `azure.location`,
                `azure.subscriptionId`,
                `azure.sdkSecretLink`,
                `azure.resourceGroup`,
                `azure.networking`,
                `azure.preInstallScript`,
                `azure.image`,
                `azure.sshKeys`,
                `azure.networkId`,
                `azure.tags`,
                `azure.autoscaler`,
              ]}
            />
          </CardTitle>
          <CardContent>
            <CpFormItem>
              <CpLabelTooltip>
                <CpLabel name={`azure.sdkSecretLink`}>Azure SDK Token Secret</CpLabel>
                <CpTooltip
                  tooltipProps={{
                    title: `An opaque secret that contains your Azure token, it will be used to manage servers in your Azure account`,
                  }}
                />
              </CpLabelTooltip>
              <CpDescription>
                <div>
                  {azureInstructionsError || !azureInstructions ? null : (
                    <span
                      className="color-link cursor-pointer"
                      onClick={() => {
                        drawer.setTemporary(
                          `mk8s-azure-sdk-token`,
                          "How to get Azure SDK Token?",
                          <>
                            <ProviderAzureInstructions
                              instructions={azureInstructions}
                              defaultSecretName={clusterName ? `${clusterName}-azure-sdk` : ""}
                              onSecretCreated={(secretName) => {
                                const itemLink = resourceLink("secret", secretName);
                                const secretState = {
                                  value: secretName,
                                  name: secretName,
                                  link: itemLink,
                                };
                                formSetValue(id, `azure.sdkSecretLink`, secretState);
                                formTriggerValidation(id);
                                drawer.closeTemporary();
                              }}
                            />
                          </>,
                          900,
                        );
                      }}
                    >
                      How to get Azure SDK Token?
                    </span>
                  )}
                </div>
              </CpDescription>
              <Space.Compact>
                <CpKindSelect
                  kind="secret"
                  name={`azure.sdkSecretLink`}
                  optionValue="name"
                  queries={[{ property: "type", value: "opaque" }]}
                  style={{ width: 600 }}
                />
                <Tooltip title={`Create an Opaque Secret`}>
                  <Button
                    onClick={() => {
                      drawer.setPersistent(
                        `mk8s-azure-create-secret`,
                        "Create Opaque Secret",
                        <>
                          <EmbeddedSecretCreate
                            onCancel={() => drawer.closePersistent()}
                            onDone={(itemLink) => {
                              drawer.closePersistent();
                              const { name } = parseLink(itemLink, { useInputCtx: true });
                              const secretState = {
                                value: name,
                                name: name,
                                link: itemLink,
                              };
                              formSetValue(id, `azure.sdkSecretLink`, secretState);
                              formTriggerValidation(id);
                            }}
                            lockedType={`opaque`}
                          />
                        </>,
                        600,
                      );
                    }}
                    icon={<PlusOutlined />}
                  />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`azure.sdkSecretLink`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`azure.location`}>Location</CpLabel>
              <CpDescription></CpDescription>
              <Space.Compact>
                <CpInput name={`azure.location`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("location")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`azure.location`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`azure.subscriptionId`}>Subscription ID</CpLabel>
              <Space.Compact>
                <CpInput name={`azure.subscriptionId`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("subscriptions")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`azure.subscriptionId`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`azure.resourceGroup`}>Resource Group</CpLabel>
              <Space.Compact>
                <CpInput name={`azure.resourceGroup`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("resourceGroups")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`azure.resourceGroup`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name={`azure.networkId`}>Network ID</CpLabel>
              <Space.Compact>
                <CpInput name={`azure.networkId`} style={{ width: 600 }} />
                <Tooltip title={`Browse`}>
                  <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("networks")} />
                </Tooltip>
              </Space.Compact>
              <CpErrorWarning name={`azure.networkId`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name="skip">Image</CpLabel>
              <CpRadioGroup
                name={`azure.image.type`}
                style={{ width: 600 }}
                options={[
                  { label: "Recommended", value: "recommended" },
                  { label: "Reference", value: "reference" },
                ]}
              />
              {imageType === "reference" ? (
                <>
                  <div className="flex flex-col mt-2">
                    <CpLabel name={`azure.image.reference.publisher`}>Publisher</CpLabel>
                    <CpInput name={`azure.image.reference.publisher`} style={{ width: 600 }} />
                  </div>
                  <div className="flex flex-col mt-2">
                    <CpLabel name={`azure.image.reference.offer`}>Offer</CpLabel>
                    <CpInput name={`azure.image.reference.offer`} style={{ width: 600 }} />
                  </div>
                  <div className="flex flex-col mt-2">
                    <CpLabel name={`azure.image.reference.sku`}>SKU</CpLabel>
                    <CpInput name={`azure.image.reference.sku`} style={{ width: 600 }} />
                  </div>
                  <div className="flex flex-col mt-2">
                    <CpLabel name={`azure.image.reference.version`}>Version</CpLabel>
                    <CpInput name={`azure.image.reference.version`} style={{ width: 600 }} />
                  </div>
                </>
              ) : imageType === "recommended" ? (
                <>
                  <CpSelect name={`azure.image.recommended`} style={{ width: 600 }} options={recommendedAmis} />
                </>
              ) : null}
              <CpErrorWarningArray
                names={[
                  `azure.image.reference.publisher`,
                  `azure.image.reference.offer`,
                  `azure.image.reference.sku`,
                  `azure.image.reference.version`,
                  `azure.image.recommended`,
                ]}
              />
            </CpFormItem>
            <div style={{ maxWidth: 900 }}>
              <CpArrayString label="SSH Keys" name={`azure.sshKeys`} />
            </div>
            <div className="mt-4">
              <CardTitle style={{ marginBottom: 0, fontSize: 20 }}>
                <div className={`flex items-baseline ${isAdvancedCollapsed ? "" : "mb-4"}`}>
                  <div
                    className={`flex items-center gap-2 color-link-hover cursor-pointer`}
                    onClick={() => {
                      setIsAdvancedCollapsed((x) => !x);
                    }}
                  >
                    <CaretRightOutlined rotate={isAdvancedCollapsed ? 0 : 90} className={`self-center`} />
                    <span>Advanced</span>
                    <CpDirtyIndicatorArrayIcon names={["provider-azure-advanced"]} />
                  </div>
                </div>
              </CardTitle>
            </div>
            <CardCollapsibleContent collapsed={isAdvancedCollapsed}>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                  label="Tags"
                  name={`azure.tags`}
                >
                  <CpArrayItemNameValuePair />
                </CpArrayObject>
              </div>
              <ProviderPreInstallScript provider="azure" />
              <ProviderNetworking provider="azure" />
              <ProviderAutoscaler provider="azure" />
            </CardCollapsibleContent>
          </CardContent>
        </Card>
      </div>
      {browserKey === "location" ? (
        <NGProviderBrowser
          title="Browse Azure Locations"
          filteredColumns={["Name"]}
          initialValue={location}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "locations",
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(value) => {
            formSetValue(id, `azure.location`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Value"]}
          getData={(item: any) => [item.location, item.displayName, item.location]}
        />
      ) : null}
      {browserKey === "resourceGroups" ? (
        <NGProviderBrowser
          title="Browse Azure Resource Groups"
          initialValue={resourceGroup}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "resourceGroups",
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(value) => {
            formSetValue(id, `azure.resourceGroup`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Id"]}
          getData={(item: any) => [item.name, item.name, item.id]}
        />
      ) : null}
      {browserKey === "networks" ? (
        <NGProviderBrowser
          title="Browse Azure Networks"
          initialValue={networkId}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "networks",
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(value) => {
            formSetValue(id, `azure.networkId`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Id", "Resource Group"]}
          getData={(item: any) => [item.id, item.name, item.id, item.resourceGroup]}
        />
      ) : null}
      {browserKey === "subscriptions" ? (
        <NGProviderBrowser
          title="Browse Azure Subscriptions"
          initialValue={subscriptionId}
          fetchData={async () => {
            const form = store.getState().form.instances[id];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "subscriptions",
                cluster: inputToMk8s(values),
              },
            });
            return data;
          }}
          onOk={(value) => {
            formSetValue(id, `azure.subscriptionId`, value as string);
            formTriggerValidation(id);
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name", "Id"]}
          getData={(item: any) => [item.id, item.displayName, item.id]}
        />
      ) : null}
    </>
  );
};
